import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";
import { tenantScopeSchema } from "../common/tenant";
import { legalPersonKindSchema } from "../loans/loan-agreements";
import { loanClauseSchema } from "../loans/loan-agreements";

/**
 * Paid monitoring service agreement (ADR-077).
 *
 * Deliberately separate from the comodato: fees live here so the equipment
 * loan stays gratuitous under article 1533 CCC (ADR-074). Amounts are USD
 * ex-IVA commercial figures from the field tariff (ADR-068).
 */

export const SERVICE_AGREEMENT_STATUSES = [
  "DRAFT",
  "ISSUED",
  "SIGNED",
  "CLOSED",
  "CANCELLED",
] as const;
export const serviceAgreementStatusSchema = z.enum(SERVICE_AGREEMENT_STATUSES);
export type ServiceAgreementStatus = z.infer<typeof serviceAgreementStatusSchema>;

/** Which states still allow the terms to be edited. */
export const SERVICE_EDITABLE_STATUSES: readonly ServiceAgreementStatus[] = ["DRAFT"];

/** Preferred field tariff defaults (ADR-068 / ADR-077). */
export const DEFAULT_SERVICE_TERM_MONTHS = 36;
export const DEFAULT_MONTHLY_ABONO_USD = 69;
export const DEFAULT_ALTA_USD = 200;
export const DEFAULT_INCLUDED_SENSORS = 4;
export const DEFAULT_EXTRA_SENSOR_USD = 12;

const moneyUsdSchema = z
  .number()
  .finite()
  .nonnegative()
  .refine((value) => Math.round(value * 100) === value * 100, {
    message: "Use at most two decimal places.",
  });

export const serviceAgreementSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  number: z.string(),
  status: serviceAgreementStatusSchema,

  providerName: z.string(),
  providerTaxId: z.string(),
  providerAddress: z.string(),

  customerKind: legalPersonKindSchema,
  customerName: z.string(),
  customerTaxId: z.string(),
  customerAddress: z.string(),

  signatoryName: z.string(),
  signatoryDocument: z.string(),
  signatoryCapacity: z.string(),

  siteId: z.string().nullable(),
  siteName: z.string(),
  siteAddress: z.string(),

  monthlyAbonoUsd: z.number(),
  altaUsd: z.number(),
  includedSensors: z.number().int(),
  extraSensorUsd: z.number().nullable(),
  currency: z.literal("USD"),

  termMonths: z.number().int(),
  startsOn: z.iso.datetime(),
  endsOn: z.iso.datetime(),

  loanAgreementId: z.string().nullable(),
  loanAgreementNumber: z.string().nullable(),

  renewedFromId: z.string().nullable(),

  signedOn: z.iso.datetime().nullable(),
  attestedAt: z.iso.datetime().nullable(),
  attestedByEmail: z.string().nullable(),
  issuedAt: z.iso.datetime().nullable(),
  closedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  notes: z.string().nullable(),

  clauses: z.array(loanClauseSchema),
  templateVersion: z.string().nullable(),
  draftNotice: z.string().nullable(),
  fileCopyNotice: z.string().nullable(),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type ServiceAgreement = z.infer<typeof serviceAgreementSchema>;

export const listServiceAgreementsQuerySchema = listQuerySchema.extend({
  ...tenantScopeSchema.shape,
  status: serviceAgreementStatusSchema.optional(),
  siteId: z.uuid().optional(),
  /** Services linked to this comodato (detail hub cross-link). */
  loanAgreementId: z.uuid().optional(),
  /**
   * Live agreements whose `endsOn` falls on or before today + N days
   * (including already overdue). Same chase window as comodato.
   */
  endingWithinDays: z.coerce.number().int().min(0).max(365).optional(),
});

export type ListServiceAgreementsQuery = z.infer<typeof listServiceAgreementsQuerySchema>;

export const paginatedServiceAgreementsSchema = paginatedSchema(serviceAgreementSchema);
export const serviceAgreementListSchema = paginatedServiceAgreementsSchema;
export type ServiceAgreementList = z.infer<typeof serviceAgreementListSchema>;

export const createServiceAgreementRequestSchema = z.object({
  organizationId: z.string().optional(),
  siteId: z.string().min(1),
  customerKind: legalPersonKindSchema,
  customerName: z.string().trim().min(2).max(200),
  customerTaxId: z.string().trim().min(1).max(40),
  customerAddress: z.string().trim().min(5).max(300),
  signatoryName: z.string().trim().min(2).max(200),
  signatoryDocument: z.string().trim().min(1).max(40),
  signatoryCapacity: z.string().trim().min(2).max(120),
  monthlyAbonoUsd: moneyUsdSchema,
  altaUsd: moneyUsdSchema,
  includedSensors: z.number().int().min(1).max(100),
  extraSensorUsd: moneyUsdSchema.nullable().optional(),
  termMonths: z.number().int().min(1).max(120).default(DEFAULT_SERVICE_TERM_MONTHS),
  startsOn: z.iso.date(),
  loanAgreementId: z.string().nullable().optional(),
  notes: z.string().trim().max(2_000).optional(),
});

export type CreateServiceAgreementRequest = z.infer<typeof createServiceAgreementRequestSchema>;

/**
 * Draft-only edits. Site and tenant stay fixed after create — same rule as
 * the comodato — so a service agreement cannot silently move customer paper
 * onto another location.
 */
export const updateServiceAgreementRequestSchema = z.object({
  customerKind: legalPersonKindSchema.optional(),
  customerName: z.string().trim().min(2).max(200).optional(),
  customerTaxId: z.string().trim().min(1).max(40).optional(),
  customerAddress: z.string().trim().min(5).max(300).optional(),
  signatoryName: z.string().trim().min(2).max(200).optional(),
  signatoryDocument: z.string().trim().min(1).max(40).optional(),
  signatoryCapacity: z.string().trim().min(2).max(120).optional(),
  monthlyAbonoUsd: moneyUsdSchema.optional(),
  altaUsd: moneyUsdSchema.optional(),
  includedSensors: z.number().int().min(1).max(100).optional(),
  extraSensorUsd: moneyUsdSchema.nullable().optional(),
  termMonths: z.number().int().min(1).max(120).optional(),
  startsOn: z.iso.date().optional(),
  loanAgreementId: z.string().nullable().optional(),
  notes: z.string().trim().max(2_000).optional(),
});

export type UpdateServiceAgreementRequest = z.infer<typeof updateServiceAgreementRequestSchema>;

export const attestServiceSignatureRequestSchema = z.object({
  signedOn: z.iso.date(),
  notes: z.string().trim().max(2_000).optional(),
});

export type AttestServiceSignatureRequest = z.infer<typeof attestServiceSignatureRequestSchema>;
